import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, updateDoc, collection, getDocs, onSnapshot, addDoc, deleteDoc, serverTimestamp, arrayUnion, query, orderBy, limit } from "firebase/firestore";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject, listAll } from "firebase/storage";

const firebaseConfig = {
  apiKey: "AIzaSyCgJTQbbkfJ0Wv19QyqtYIIIaCbmmV4g2o",
  authDomain: "dorcusstep.firebaseapp.com",
  projectId: "dorcusstep",
  storageBucket: "dorcusstep.firebasestorage.app",
  messagingSenderId: "316862716544",
  appId: "1:316862716544:web:533c3f8010afde82e56465",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export { storageRef as ref, uploadBytesResumable, getDownloadURL, deleteObject };
export const googleProvider = new GoogleAuthProvider();

export const HOUSE_COMPUTER_EMAILS = {
  "ra.housea@dorcushomeva.org": "Dorcus 1 - 801",
  "ra.houseb@dorcushomeva.org": "Dorcus 2 - 24th St",
};
export const isHouseComputerAccount = (email) => email in HOUSE_COMPUTER_EMAILS;
export const facilityForHouseAccount = (email) => HOUSE_COMPUTER_EMAILS[email] || null;

const BOOTSTRAP_ADMIN = "emajor@dorcushomeva.org";

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}
export async function logOut() { await signOut(auth); }

export async function getOrCreateUserProfile(firebaseUser) {
  const ref = doc(db, "users", firebaseUser.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data();
  const isBootstrap = firebaseUser.email === BOOTSTRAP_ADMIN;
  const isHouse = isHouseComputerAccount(firebaseUser.email);
  const profile = {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || firebaseUser.email,
    photoURL: firebaseUser.photoURL || null,
    role: isBootstrap ? "admin" : isHouse ? "house_computer" : "pending",
    facility: isHouse ? facilityForHouseAccount(firebaseUser.email) : null,
    createdAt: serverTimestamp(),
    approvedAt: (isBootstrap || isHouse) ? serverTimestamp() : null,
  };
  await setDoc(ref, profile);
  return profile;
}

export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export async function updateUserRole(uid, role, facility = null) {
  await updateDoc(doc(db, "users", uid), { role, facility: facility || null, approvedAt: serverTimestamp() });
}

export async function getAllUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map(d => d.data());
}

function hashPin(pin) {
  let hash = 0;
  const str = "dorcus_salt_" + pin;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

export async function createRAStaff({ name, facility, pin }) {
  const ref = await addDoc(collection(db, "raStaff"), {
    name, facility, pinHash: hashPin(pin), active: true, createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateRAStaff(id, updates) {
  const data = { ...updates };
  if (updates.pin) { data.pinHash = hashPin(updates.pin); delete data.pin; }
  await updateDoc(doc(db, "raStaff", id), data);
}

export async function deleteRAStaff(id) {
  await deleteDoc(doc(db, "raStaff", id));
}

export function subscribeToRAStaff(facility, callback) {
  return onSnapshot(collection(db, "raStaff"), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const filtered = facility ? all.filter(r => r.facility === facility && r.active !== false) : all;
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    callback(filtered);
  });
}

export async function verifyRAPIN(raId, pin) {
  const snap = await getDoc(doc(db, "raStaff", raId));
  if (!snap.exists()) return false;
  const data = snap.data();
  return data.pinHash === hashPin(pin) && data.active !== false;
}

export function subscribeToResidents(callback) {
  return onSnapshot(collection(db, "residents"), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function saveResident(resident) {
  const { id, ...data } = resident;
  if (id && typeof id === "string" && id.length > 0) {
    await updateDoc(doc(db, "residents", id), { ...data, updatedAt: serverTimestamp() });
    return id;
  } else {
    const ref = await addDoc(collection(db, "residents"), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return ref.id;
  }
}

export async function deleteResident(id) { await deleteDoc(doc(db, "residents", id)); }

export async function saveHeadcount(entry) {
  await addDoc(collection(db, "headcounts"), { ...entry, createdAt: serverTimestamp() });
}

export function subscribeToHeadcounts(facility, callback) {
  return onSnapshot(collection(db, "headcounts"), snap => {
    const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const filtered = facility ? all.filter(h => h.facility === facility || h.facility === "All Facilities") : all;
    filtered.sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || ""));
    callback(filtered);
  });
}

export async function saveShiftNote(note) {
  await addDoc(collection(db, "shiftNotes"), { ...note, createdAt: serverTimestamp() });
}

export function subscribeToShiftNotes(callback) {
  return onSnapshot(collection(db, "shiftNotes"), snap => {
    const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(notes);
  });
}

export async function uploadDocument(entityType, entityId, checklistIndex, file, onProgress) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `documents/${entityType}/${entityId}/${checklistIndex}/${Date.now()}_${safeName}`;
  const sRef = storageRef(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(sRef, file);
    task.on("state_changed",
      snap => onProgress && onProgress(Math.round(snap.bytesTransferred / snap.totalBytes * 100)),
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve({ url, path, name: file.name, size: file.size, type: file.type, uploadedAt: new Date().toISOString() });
      }
    );
  });
}

export async function deleteDocument(path) {
  try { await deleteObject(storageRef(storage, path)); } catch (e) { console.warn("Delete failed:", e); }
}

export async function getDocumentsForEntity(entityType, entityId) {
  try {
    const listRef = storageRef(storage, `documents/${entityType}/${entityId}`);
    const result = await listAll(listRef);
    const files = await Promise.all(
      result.items.map(async item => ({
        path: item.fullPath,
        name: item.name,
        url: await getDownloadURL(item),
      }))
    );
    return files;
  } catch { return []; }
}

export function subscribeToReferrals(callback) {
  return onSnapshot(collection(db, "referrals"), snap => {
    const referrals = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    referrals.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(referrals);
  });
}

export async function saveReferral(referral) {
  const { id, ...data } = referral;
  if (id && typeof id === "string" && id.length > 0 && !id.startsWith("temp_")) {
    await updateDoc(doc(db, "referrals", id), { ...data, updatedAt: serverTimestamp() });
    return id;
  } else {
    const ref = await addDoc(collection(db, "referrals"), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    return ref.id;
  }
}

export async function deleteReferral(id) {
  await deleteDoc(doc(db, "referrals", id));
}

export async function saveInOutEvent(event) {
  await addDoc(collection(db, "inOutLogs"), { ...event, createdAt: serverTimestamp() });
}

export function subscribeToInOutLogs(residentId, callback) {
  return onSnapshot(
    collection(db, "inOutLogs"),
    snap => {
      const logs = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => !residentId || l.residentId === residentId)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      callback(logs);
    }
  );
}

export function subscribeToTodayInOutLogs(facility, callback) {
  const today = new Date().toLocaleDateString("en-CA", {timeZone: "America/New_York"});
  const STATUS_EVENTS = ["sign_in", "sign_out"];
  return onSnapshot(collection(db, "inOutLogs"), snap => {
    const latestByResident = {};
    snap.docs.forEach(d => {
      const log = { id: d.id, ...d.data() };
      if (log.date !== today) return;
      if (facility && log.facility !== facility) return;
      if (!STATUS_EVENTS.includes(log.eventType)) return;
      const existing = latestByResident[log.residentId];
      if (!existing) { latestByResident[log.residentId] = log; return; }
      const aTs = log.createdAt?.seconds || 0;
      const bTs = existing.createdAt?.seconds || 0;
      if (aTs > bTs) { latestByResident[log.residentId] = log; return; }
      if (aTs === bTs && (log.id || "") > (existing.id || "")) {
        latestByResident[log.residentId] = log;
      }
    });
    callback(Object.values(latestByResident));
  });
}

export async function saveMedicationLog(log) {
  const existing = await getDocs(collection(db, "medicationLogs"));
  const match = existing.docs.find(d => {
    const data = d.data();
    return data.date === log.date && data.residentId === log.residentId && data.medication === log.medication;
  });
  if (match) {
    await updateDoc(doc(db, "medicationLogs", match.id), { ...log, updatedAt: serverTimestamp() });
  } else {
    await addDoc(collection(db, "medicationLogs"), { ...log, createdAt: serverTimestamp() });
  }
}

export function subscribeToMedicationLogs(date, facility, callback) {
  return onSnapshot(collection(db, "medicationLogs"), snap => {
    const logs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(l => (!date || l.date === date) && (!facility || l.facility === facility));
    callback(logs);
  });
}

export async function saveUAResult(result) {
  const existing = await getDocs(collection(db, "uaResults"));
  const match = existing.docs.find(d => {
    const data = d.data();
    return data.date === result.date && data.residentId === result.residentId;
  });
  if (match) {
    await updateDoc(doc(db, "uaResults", match.id), { ...result, updatedAt: serverTimestamp() });
  } else {
    await addDoc(collection(db, "uaResults"), { ...result, createdAt: serverTimestamp() });
  }
}

export function subscribeToUAResults(weekStart, weekEnd, facility, callback) {
  return onSnapshot(collection(db, "uaResults"), snap => {
    const results = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(l => {
        if (facility && l.facility !== facility) return false;
        if (weekStart && l.date < weekStart) return false;
        if (weekEnd && l.date > weekEnd) return false;
        return true;
      });
    callback(results);
  });
}

export function subscribeToResidentUAHistory(residentId, callback) {
  return onSnapshot(collection(db, "uaResults"), snap => {
    const results = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(l => l.residentId === residentId)
      .sort((a, b) => b.date.localeCompare(a.date));
    callback(results);
  });
}

export async function saveBACResult(result) {
  await addDoc(collection(db, "bacResults"), { ...result, createdAt: serverTimestamp() });
}

export function subscribeToBACResults(facility, callback) {
  return onSnapshot(collection(db, "bacResults"), snap => {
    const results = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => !facility || r.facility === facility)
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(results);
  });
}

export async function saveDischargeRecord(record) {
  await addDoc(collection(db, "dischargeRecords"), { ...record, createdAt: serverTimestamp() });
}

export function subscribeToDischargeRecords(callback) {
  return onSnapshot(collection(db, "dischargeRecords"), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0)));
  });
}

export async function saveAbscondNotification(record) {
  await addDoc(collection(db, "abscondNotifications"), { ...record, createdAt: serverTimestamp() });
}

export async function updateAbscondNotification(id, updates) {
  const { doc, updateDoc } = await import("firebase/firestore");
  await updateDoc(doc(db, "abscondNotifications", id), updates);
}

export function subscribeToAbscondNotifications(callback) {
  return onSnapshot(collection(db, "abscondNotifications"), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0)));
  });
}

export async function logAuditEvent(event) {
  try {
    await addDoc(collection(db, "auditLogs"), {
      ...event,
      createdAt: serverTimestamp(),
    });
  } catch(e) { console.error("Audit log failed:", e); }
}

export async function saveUnlockPin(userId, pin) {
  const { doc, setDoc } = await import("firebase/firestore");
  await setDoc(doc(db, "unlockPins", userId), { pin, updatedAt: serverTimestamp() });
}

export async function verifyUnlockPin(userId, pin) {
  const snap = await getDoc(doc(db, "unlockPins", userId));
  if (!snap.exists()) return "no_pin";
  return snap.data().pin === pin ? "valid" : "invalid";
}

export async function saveResidentDocument(docData) {
  const ref = doc(db, "residentDocuments", docData.residentId);
  await updateDoc(ref, { docs: arrayUnion(docData) }).catch(async () => {
    await setDoc(ref, { residentId: docData.residentId, docs: [docData] });
  });
}

export async function deleteResidentDocument(residentId, docId) {
  const ref = doc(db, "residentDocuments", residentId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const existing = snap.data().docs || [];
  await updateDoc(ref, { docs: existing.filter(d => d.id !== docId) });
}

export function subscribeToResidentDocuments(residentId, callback) {
  const ref = doc(db, "residentDocuments", residentId);
  return onSnapshot(ref, snap => {
    callback(snap.exists() ? (snap.data().docs || []) : []);
  });
}

export async function saveNotification(data) {
  try {
    await addDoc(collection(db, "notifications"), {
      ...data,
      createdAtServer: serverTimestamp(),
    });
  } catch(e) { console.error("saveNotification error", e); }
}

export function subscribeToNotifications(callback) {
  const q = query(collection(db, "notifications"), orderBy("createdAt","desc"), limit(50));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id:d.id, ...d.data() })));
  });
}

export async function markNotificationRead(id) {
  try {
    await updateDoc(doc(db, "notifications", id), { read: true });
  } catch(e) { console.error("markNotificationRead error", e); }
}

export async function actionNotification(id, actionData={}) {
  try {
    await updateDoc(doc(db, "notifications", id), { actioned: true, read: true, ...actionData });
  } catch(e) { console.error("actionNotification error", e); }
}